/**
 * Sync, write-back and tenancy contracts (BUILD-PLAN 2.4, 2.5, 5.3, 5.7).
 *
 * This file holds the shapes every wave-1 module shares:
 *
 * - tenancy columns (`practiceId`, `locationId`; ADR-0012),
 * - row metadata and the **sync envelope** carried by every PMS-mirrored row
 *   (BUILD-PLAN 2.4 "Every PMS-mirrored row also carries the sync envelope"),
 * - the write-back state shown on every record (BUILD-PLAN 2.5.1 rule 4),
 * - the `Command` outbox shape and `CommandStatus` lifecycle (BUILD-PLAN
 *   2.5.3, 2.5.4; frozen per 5.7 and versioned by `schemaVersion`),
 * - the per-office `Capability` manifest (BUILD-PLAN 2.5.1 rule 5, M02),
 * - `Conflict` records (BUILD-PLAN 2.5.5).
 *
 * Naming: JSON contracts use camelCase (as `core.ts` does); the Postgres
 * columns are the snake_case equivalents (`practiceId` is `practice_id`,
 * `pendingCommandId` is `pending_command_id`). Enum string values are
 * identical in both.
 *
 * This package is a leaf: it depends on zod only, never on `@velora/domain`
 * or `@velora/pms-gateway`. The PulseSync wire format (snake_case v2
 * envelope) is the gateway's concern; these are Velora's own shapes.
 */
import { z } from 'zod';

/* ------------------------------------------------------------------------ */
/* Primitives                                                                */
/* ------------------------------------------------------------------------ */

/** RFC 9562 UUID. All Velora row ids. */
export const IdSchema = z.uuid();
export type Id = z.infer<typeof IdSchema>;

/** ISO 8601 instant with an explicit offset (`Z` or `+hh:mm`). */
export const InstantSchema = z.iso.datetime({ offset: true });
export type Instant = z.infer<typeof InstantSchema>;

/** ISO 8601 calendar date `YYYY-MM-DD` (an office-local date, no zone). */
export const LocalDateSchema = z.iso.date();
export type LocalDate = z.infer<typeof LocalDateSchema>;

/**
 * IANA time zone name (`America/Detroit`), validated against the runtime's
 * tz database through `Intl.DateTimeFormat`, so a typo fails at the boundary
 * instead of silently falling back to UTC.
 */
export function isIanaTimeZone(tz: string): boolean {
  if (!/^[A-Za-z][A-Za-z0-9_+-]*(?:\/[A-Za-z0-9_+-]+)*$/.test(tz)) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}
export const IanaTimeZoneSchema = z
  .string()
  .min(1)
  .max(64)
  .refine(isIanaTimeZone, 'timezone must be an IANA zone name');

/** Row version: bumped on every write (BUILD-PLAN 2.4). Used as `expectedVersion`. */
export const RowVersionSchema = z.number().int().positive();
export type RowVersion = z.infer<typeof RowVersionSchema>;

/**
 * Dotted snake_case entity name used by commands, conflicts and audit
 * (`appointment`, `contact_point`). Mirrors `EntityKeySchema` in core.ts.
 */
export const EntityNameSchema = z
  .string()
  .regex(/^[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)*$/, 'entity must be dotted snake_case');
export type EntityName = z.infer<typeof EntityNameSchema>;

/* ------------------------------------------------------------------------ */
/* Tenancy (ADR-0012)                                                        */
/* ------------------------------------------------------------------------ */

/** Columns of a row that belongs to exactly one office (scope `location`). */
export const LocationOwnedSchema = z.object({
  practiceId: IdSchema,
  locationId: IdSchema,
});
export type LocationOwned = z.infer<typeof LocationOwnedSchema>;

/** Columns of a row shared by a practice's offices (scope `practice`). */
export const PracticeOwnedSchema = z.object({
  practiceId: IdSchema,
});
export type PracticeOwned = z.infer<typeof PracticeOwnedSchema>;

/**
 * Columns of a row that is practice-wide when `locationId` is null and one
 * office's row otherwise (scope `practice_optional_location`).
 */
export const PracticeOptionalLocationSchema = z.object({
  practiceId: IdSchema,
  locationId: IdSchema.nullable(),
});
export type PracticeOptionalLocation = z.infer<typeof PracticeOptionalLocationSchema>;

/* ------------------------------------------------------------------------ */
/* Row metadata (BUILD-PLAN 2.4)                                             */
/* ------------------------------------------------------------------------ */

/** created/updated stamps, version and soft delete carried by every tenant row. */
export const RowMetaSchema = z.object({
  createdAt: InstantSchema,
  createdBy: IdSchema.nullable(),
  updatedAt: InstantSchema,
  updatedBy: IdSchema.nullable(),
  version: RowVersionSchema,
  /** Soft delete. Rows are never hard-deleted while retention applies. */
  deletedAt: InstantSchema.nullable(),
});
export type RowMeta = z.infer<typeof RowMetaSchema>;

/* ------------------------------------------------------------------------ */
/* PMS identity and ownership stages (ADR-0001, ADR-0005)                    */
/* ------------------------------------------------------------------------ */

export const PMS_SYSTEMS = ['opendental', 'dentrix', 'eaglesoft'] as const;
export const PmsSystemSchema = z.enum(PMS_SYSTEMS);
export type PmsSystem = z.infer<typeof PmsSystemSchema>;

export const PMS_ADAPTERS = ['mock', 'pulsesync-v1', 'pulsesync-v2'] as const;
export const PmsAdapterSchema = z.enum(PMS_ADAPTERS);
export type PmsAdapter = z.infer<typeof PmsAdapterSchema>;

/** Where a row was born. `pms` rows must carry a PMS identity. */
export const RowOriginSchema = z.enum(['pms', 'velora']);
export type RowOrigin = z.infer<typeof RowOriginSchema>;

/**
 * System-of-record stage per office and data class (BUILD-PLAN 1.2):
 * S0 Overlay, S1 Front office, S2 Clinical, S3 Financial, S4 Retire.
 */
export const SOR_STAGES = ['S0', 'S1', 'S2', 'S3', 'S4'] as const;
export const SorStageSchema = z.enum(SOR_STAGES);
export type SorStage = z.infer<typeof SorStageSchema>;

/**
 * Data classes whose ownership migrates independently (BUILD-PLAN 1.2,
 * 2.5.5). `engagement` (threads, calls, reviews, leads, tasks) is always
 * Velora-owned and never written to the PMS.
 */
export const DATA_CLASSES = [
  'appointments',
  'demographics',
  'recall',
  'clinical',
  'imaging',
  'money',
  'engagement',
] as const;
export const DataClassSchema = z.enum(DATA_CLASSES);
export type DataClass = z.infer<typeof DataClassSchema>;

/**
 * The stage at which Velora becomes system of record for each data class
 * (BUILD-PLAN 1.2 stage table). Before this stage the PMS wins and Velora
 * writes are commands; from it on, Velora wins.
 */
export const SOR_STAGE_FOR_CLASS: Readonly<Record<DataClass, SorStage>> = {
  engagement: 'S0',
  appointments: 'S1',
  demographics: 'S1',
  recall: 'S1',
  clinical: 'S2',
  imaging: 'S2',
  money: 'S3',
};

const SOR_ORDER: Readonly<Record<SorStage, number>> = { S0: 0, S1: 1, S2: 2, S3: 3, S4: 4 };

/** True when Velora is the system of record for `dataClass` at `stage`. */
export function isVeloraSystemOfRecord(dataClass: DataClass, stage: SorStage): boolean {
  return SOR_ORDER[stage] >= SOR_ORDER[SOR_STAGE_FOR_CLASS[dataClass]];
}

/* ------------------------------------------------------------------------ */
/* Write-back state (BUILD-PLAN 2.5.1 rule 4)                                */
/* ------------------------------------------------------------------------ */

/**
 * What the UI shows on every record (the WriteBackDot):
 * - `synced`: Velora and the PMS agree (read-back confirmed).
 * - `pending`: a command is queued in Velora's outbox.
 * - `sent`: the adapter delivered it; no ack yet.
 * - `applied`: the PMS acked; waiting for the next read to confirm.
 * - `conflict`: rejected for a version or slot clash; see the conflict inbox.
 * - `failed`: rejected, failed or expired; Velora's optimistic state reverted.
 * - `unknown`: `sent` for more than 5 minutes; never auto-retried for money.
 */
export const WRITE_BACK_STATES = [
  'synced',
  'pending',
  'sent',
  'applied',
  'conflict',
  'failed',
  'unknown',
] as const;
export const WriteBackStateSchema = z.enum(WRITE_BACK_STATES);
export type WriteBackState = z.infer<typeof WriteBackStateSchema>;

/* ------------------------------------------------------------------------ */
/* Sync envelope (BUILD-PLAN 2.4)                                            */
/* ------------------------------------------------------------------------ */

/**
 * Carried by every PMS-mirrored row. Matches the columns that
 * `meta.setup_tenant_table(..., p_mirrored => true)` adds in the core
 * migrations. `pmsVersion` is the source row stamp or hash, opaque to Velora.
 */
export const SyncEnvelopeShape = {
  origin: RowOriginSchema,
  pmsSystem: PmsSystemSchema.nullable(),
  pmsLinkId: IdSchema.nullable(),
  /** PMS primary key as a string (OD AptNum, Dentrix appt id, ...). */
  pmsId: z.string().min(1).max(64).nullable(),
  pmsVersion: z.string().min(1).max(128).nullable(),
  sourceUpdatedAt: InstantSchema.nullable(),
  syncedAt: InstantSchema.nullable(),
  sorStage: SorStageSchema.nullable(),
  /** Set while a command for this row is in flight (BUILD-PLAN 2.5.4 step 2). */
  pendingCommandId: IdSchema.nullable(),
} as const;

export const SyncEnvelopeSchema = z.object(SyncEnvelopeShape).superRefine((e, ctx) => {
  refineSyncEnvelope(e, ctx);
});
export type SyncEnvelope = z.infer<typeof SyncEnvelopeSchema>;

/**
 * Envelope invariant, mirroring the `<table>_envelope_ck` check constraint:
 * a row that came from the PMS must name its PMS system, link and id.
 * Exported so entity schemas that spread `SyncEnvelopeShape` can reuse it.
 */
export function refineSyncEnvelope(
  e: {
    origin: RowOrigin;
    pmsSystem: PmsSystem | null;
    pmsLinkId: string | null;
    pmsId: string | null;
  },
  ctx: z.RefinementCtx,
): void {
  if (e.origin === 'pms') {
    for (const key of ['pmsSystem', 'pmsLinkId', 'pmsId'] as const) {
      if (e[key] === null) {
        ctx.addIssue({
          code: 'custom',
          path: [key],
          message: `${key} is required when origin is "pms"`,
        });
      }
    }
  }
}

/* ------------------------------------------------------------------------ */
/* Commands (BUILD-PLAN 2.5.3, 2.5.4)                                        */
/* ------------------------------------------------------------------------ */

/**
 * Velora's command vocabulary, `entity.verb`. The pulsesync-v1 adapter maps
 * these onto today's `command_type` names; pulsesync-v2 sends them as-is.
 * Adding a type is additive; renaming one needs a `schemaVersion` bump.
 */
export const COMMAND_TYPES = [
  // Scheduling (data class: appointments)
  'appointment.create',
  'appointment.update',
  'appointment.cancel',
  'appointment.confirm',
  'appointment.unconfirm',
  // Patient flow step (NEW-WF-06; held by WRITE_READY)
  'appointment.flow',
  // Demographics
  'patient.create',
  'patient.update',
  // Recall
  'recall.update',
  'recall.sent',
  // Clinical
  'note.add',
  'procedure.create',
  'procedure.update',
  // Engagement mirrored into the PMS communication log (texting-inbox 4.9)
  'commlog.add',
  // Money: blocked on every v1 adapter (BUILD-PLAN 2.5.3 allow-list)
  'payment.post',
  'adjustment.post',
  'insurance_payment.post',
  'claim.create',
  'claim.update',
  'treatment_plan.create',
  'treatment_plan.update',
  // Staff time clock punch to the PMS time clock (timeclock PMS sync; held by WRITE_READY)
  'timeclock.punch',
] as const;
export const CommandTypeSchema = z.enum(COMMAND_TYPES);
export type CommandType = z.infer<typeof CommandTypeSchema>;

/**
 * Money-moving command types. They are never auto-retried from `unknown`
 * (BUILD-PLAN 2.5.4 step 6) and require `isMoney: true` on the command row.
 */
export const MONEY_COMMAND_TYPES: ReadonlySet<CommandType> = new Set<CommandType>([
  'payment.post',
  'adjustment.post',
  'insurance_payment.post',
  'claim.create',
  'claim.update',
  'treatment_plan.create',
  'treatment_plan.update',
]);

export function isMoneyCommandType(type: CommandType): boolean {
  return MONEY_COMMAND_TYPES.has(type);
}

/** The data class whose SoR stage governs a command type. */
export const COMMAND_DATA_CLASS: Readonly<Record<CommandType, DataClass>> = {
  'appointment.create': 'appointments',
  'appointment.update': 'appointments',
  'appointment.cancel': 'appointments',
  'appointment.confirm': 'appointments',
  'appointment.unconfirm': 'appointments',
  'appointment.flow': 'appointments',
  'patient.create': 'demographics',
  'patient.update': 'demographics',
  'recall.update': 'recall',
  'recall.sent': 'recall',
  'note.add': 'clinical',
  'procedure.create': 'clinical',
  'procedure.update': 'clinical',
  'commlog.add': 'engagement',
  'payment.post': 'money',
  'adjustment.post': 'money',
  'insurance_payment.post': 'money',
  'claim.create': 'money',
  'claim.update': 'money',
  'treatment_plan.create': 'clinical',
  'treatment_plan.update': 'clinical',
  // Staff time is Velora-owned from S0, like engagement (no patient data).
  'timeclock.punch': 'engagement',
};

/**
 * Outbox lifecycle. Matches `command.status` in the core platform migration
 * and the PulseSync v2 statuses (`queued → dispatched → applied |
 * rejected_conflict | failed | expired`) plus Velora's own `sent` (adapter
 * delivered) and `unknown` (sent > 5 min without an ack).
 */
export const COMMAND_STATUSES = [
  'queued',
  'dispatched',
  'sent',
  'applied',
  'rejected_conflict',
  'failed',
  'expired',
  'unknown',
] as const;
export const CommandStatusSchema = z.enum(COMMAND_STATUSES);
export type CommandStatus = z.infer<typeof CommandStatusSchema>;

/** Statuses after which nothing more happens to the command. */
export const TERMINAL_COMMAND_STATUSES: ReadonlySet<CommandStatus> = new Set<CommandStatus>([
  'applied',
  'rejected_conflict',
  'failed',
  'expired',
]);

/**
 * Allowed transitions. `unknown -> dispatched` is the non-money retry with the
 * same idempotency key after a read-back (BUILD-PLAN 2.5.4 step 6); use
 * `canRetryUnknown` to apply the money rule.
 */
export const COMMAND_STATUS_TRANSITIONS: Readonly<Record<CommandStatus, readonly CommandStatus[]>> =
  {
    queued: ['dispatched', 'failed', 'expired'],
    dispatched: ['sent', 'failed', 'expired'],
    sent: ['applied', 'rejected_conflict', 'failed', 'unknown'],
    unknown: ['applied', 'rejected_conflict', 'failed', 'dispatched'],
    applied: [],
    rejected_conflict: [],
    failed: [],
    expired: [],
  };

/**
 * Whether `from -> to` is a legal lifecycle step. The re-dispatch from
 * `unknown` is only legal for a command known to be non-money (BUILD-PLAN
 * 2.5.1 rule 4, 2.5.4 step 6: "never auto-retry money"); when the caller does
 * not say, it is refused (fail closed). Money commands left in `unknown` are
 * reconciled by a human against the PMS ledger.
 */
export function canTransitionCommand(
  from: CommandStatus,
  to: CommandStatus,
  opts?: { isMoney: boolean },
): boolean {
  if (from === 'unknown' && to === 'dispatched' && (opts === undefined || opts.isMoney)) {
    return false;
  }
  return COMMAND_STATUS_TRANSITIONS[from].includes(to);
}

/** Commands in `sent` longer than this become `unknown` (BUILD-PLAN 2.5.1, M02). */
export const COMMAND_UNKNOWN_AFTER_SECONDS = 300;

/**
 * Whether an `unknown` command may be re-dispatched automatically. Money is
 * never auto-retried; everything else only after a read-back showed the
 * change did not land.
 */
export function canRetryUnknown(
  command: { isMoney: boolean },
  readBackShowsApplied: boolean,
): boolean {
  return !command.isMoney && !readBackShowsApplied;
}

/** The record-level write-back state for a command in a given status. */
export function writeBackStateForCommand(status: CommandStatus): WriteBackState {
  switch (status) {
    case 'queued':
    case 'dispatched':
      return 'pending';
    case 'sent':
      return 'sent';
    case 'applied':
      return 'applied';
    case 'rejected_conflict':
      return 'conflict';
    case 'failed':
    case 'expired':
      return 'failed';
    case 'unknown':
      return 'unknown';
  }
}

/** The human behind every PMS write (BUILD-PLAN 2.5.1 rule 3, 2.7). */
export const RequestedBySchema = z.object({
  userId: IdSchema,
  /** Denormalised display name, kept for the audit trail. */
  name: z.string().min(1).max(200),
});
export type RequestedBy = z.infer<typeof RequestedBySchema>;

/**
 * Idempotency key, one per user intent (BUILD-PLAN 2.5.3 `vel-<uuid>`).
 * 8..200 characters to match the `command` check constraint.
 */
export const IdempotencyKeySchema = z
  .string()
  .min(8)
  .max(200)
  .regex(/^[A-Za-z0-9._:-]+$/, 'idempotency key must be URL-safe');
export type IdempotencyKey = z.infer<typeof IdempotencyKeySchema>;

/** What the command acts on. `expectedVersion` is the PMS row stamp from the last read. */
export const CommandTargetSchema = z
  .object({
    entity: EntityNameSchema,
    /** Velora row id; null for a create until the row exists. */
    rowId: IdSchema.nullable(),
    /** PMS id; null for a create. */
    pmsId: z.string().min(1).max(64).nullable(),
    /** `pmsVersion` of the row as Velora last read it (conflict precheck). */
    expectedVersion: z.string().min(1).max(128).nullable(),
  })
  .strict();
export type CommandTarget = z.infer<typeof CommandTargetSchema>;

/** Field changes. Typed per command in module contracts (see schedule.ts). */
export const CommandChangesSchema = z.record(z.string(), z.unknown());
export type CommandChanges = z.infer<typeof CommandChangesSchema>;

/** Adapter result on `applied` (BUILD-PLAN 2.5.3 `result`). */
export const CommandResultSchema = z.object({
  /** New PMS ids by entity, for example `{ appointment: "98765" }`. */
  pmsIds: z.record(z.string(), z.string()),
  /** The fresh canonical row as read back from the PMS, when available. */
  row: z.record(z.string(), z.unknown()).nullable(),
  pmsVersion: z.string().nullable(),
});
export type CommandResult = z.infer<typeof CommandResultSchema>;

export const COMMAND_ERROR_CODES = [
  'capability_unavailable',
  'writes_paused',
  'version_conflict',
  'slot_conflict',
  'validation_failed',
  'not_found',
  'pms_rejected',
  'pms_unreachable',
  'timeout',
  'expired',
  'internal',
] as const;
export const CommandErrorCodeSchema = z.enum(COMMAND_ERROR_CODES);
export type CommandErrorCode = z.infer<typeof CommandErrorCodeSchema>;

/** Failure detail. `message` must not contain PHI (BUILD-PLAN 2.8). */
export const CommandErrorSchema = z.object({
  code: CommandErrorCodeSchema,
  message: z.string().max(500),
  /** Raw adapter code for support, never shown to staff. */
  adapterCode: z.string().max(100).nullable(),
});
export type CommandError = z.infer<typeof CommandErrorSchema>;

/**
 * What a module service submits to the outbox. The API fills in tenancy,
 * `requestedBy`, `isMoney` and timestamps.
 */
export const CommandRequestSchema = z
  .object({
    type: CommandTypeSchema,
    idempotencyKey: IdempotencyKeySchema,
    target: CommandTargetSchema,
    changes: CommandChangesSchema,
  })
  .strict();
export type CommandRequest = z.infer<typeof CommandRequestSchema>;

/** Current version of the command envelope (BUILD-PLAN 5.7: frozen and versioned). */
export const COMMAND_SCHEMA_VERSION = 1;

/** A stored outbox row as the API returns it. */
export const CommandSchema = z
  .object({
    id: IdSchema,
    ...LocationOwnedSchema.shape,
    pmsLinkId: IdSchema,
    type: CommandTypeSchema,
    schemaVersion: z.number().int().min(1),
    idempotencyKey: IdempotencyKeySchema,
    requestedBy: RequestedBySchema,
    target: CommandTargetSchema,
    changes: CommandChangesSchema,
    isMoney: z.boolean(),
    status: CommandStatusSchema,
    attempts: z.number().int().nonnegative(),
    /** Command id assigned by PulseSync, once dispatched. */
    pmsCommandId: z.string().nullable(),
    createdAt: InstantSchema,
    sentAt: InstantSchema.nullable(),
    finishedAt: InstantSchema.nullable(),
    result: CommandResultSchema.nullable(),
    error: CommandErrorSchema.nullable(),
  })
  .superRefine((c, ctx) => {
    if (c.isMoney !== isMoneyCommandType(c.type)) {
      ctx.addIssue({
        code: 'custom',
        path: ['isMoney'],
        message: `isMoney must be ${String(isMoneyCommandType(c.type))} for ${c.type}`,
      });
    }
    if (
      (['sent', 'applied', 'rejected_conflict', 'unknown'] as CommandStatus[]).includes(c.status) &&
      c.sentAt === null
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['sentAt'],
        message: `sentAt is required in ${c.status}`,
      });
    }
    const terminal = TERMINAL_COMMAND_STATUSES.has(c.status);
    if (terminal !== (c.finishedAt !== null)) {
      ctx.addIssue({
        code: 'custom',
        path: ['finishedAt'],
        message: terminal
          ? `finishedAt is required in terminal status ${c.status}`
          : `finishedAt must be null in ${c.status}`,
      });
    }
    if (c.status === 'applied' && c.result === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['result'],
        message: 'applied commands carry a result',
      });
    }
    if ((c.status === 'failed' || c.status === 'rejected_conflict') && c.error === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['error'],
        message: `${c.status} commands carry an error`,
      });
    }
    if (c.status === 'applied' && c.error !== null) {
      ctx.addIssue({ code: 'custom', path: ['error'], message: 'applied commands have no error' });
    }
    if (c.status !== 'applied' && c.result !== null) {
      ctx.addIssue({
        code: 'custom',
        path: ['result'],
        message: 'only applied commands carry a result',
      });
    }
    const created = Date.parse(c.createdAt);
    if (c.sentAt !== null && Date.parse(c.sentAt) < created) {
      ctx.addIssue({ code: 'custom', path: ['sentAt'], message: 'sentAt is before createdAt' });
    }
    if (c.finishedAt !== null) {
      const floor = c.sentAt !== null ? Date.parse(c.sentAt) : created;
      if (Date.parse(c.finishedAt) < floor) {
        ctx.addIssue({
          code: 'custom',
          path: ['finishedAt'],
          message: 'finishedAt is before the command was created or sent',
        });
      }
    }
  });
export type Command = z.infer<typeof CommandSchema>;

/** The 202 body after a module submits a command (BUILD-PLAN 2.5.3). */
export const CommandAcceptedSchema = z.object({
  commandId: IdSchema,
  status: CommandStatusSchema,
  writeBack: WriteBackStateSchema,
  /** True when this idempotency key was seen before and the original is returned. */
  replayed: z.boolean(),
});
export type CommandAccepted = z.infer<typeof CommandAcceptedSchema>;

/* ------------------------------------------------------------------------ */
/* Capabilities (BUILD-PLAN 2.5.1 rule 5, 2.5.3 allow-list, M02)             */
/* ------------------------------------------------------------------------ */

/**
 * - `stable`: the adapter's default tier.
 * - `experimental`: needs an opt-in on the connector; never trusted until
 *   verified per office (BUILD-PLAN 1.3 Gate C).
 * - `blocked`: Velora refuses it on this PMS (known wrong behaviour).
 * - `unsupported`: the PMS or adapter cannot do it at all.
 */
export const CAPABILITY_TIERS = ['stable', 'experimental', 'blocked', 'unsupported'] as const;
export const CapabilityTierSchema = z.enum(CAPABILITY_TIERS);
export type CapabilityTier = z.infer<typeof CapabilityTierSchema>;

/** One command's capability for one office. */
export const CapabilitySchema = z
  .object({
    commandType: CommandTypeSchema,
    tier: CapabilityTierSchema,
    /** Proven by a read-back on this office's PMS version (sandbox or live). */
    verified: z.boolean(),
    verifiedAt: InstantSchema.nullable(),
    verifiedPmsVersion: z.string().nullable(),
    /**
     * Change fields the adapter silently ignores; the UI must disable editing
     * them (for example OD `update_appointment` ignores `end_time`, so
     * `lengthMin` and `timePattern` are listed and resize is disabled).
     */
    ignoredFields: z.array(z.string().min(1)),
    /**
     * Per-PMS semantics staff must see before confirming, for example
     * "Cancel is recorded as Broken in Open Dental".
     */
    semanticsNote: z.string().max(300).nullable(),
    /** Why the action is unavailable; shown as the disabled-control tooltip. */
    reason: z.string().max(300).nullable(),
  })
  .superRefine((c, ctx) => {
    if (c.verified && (c.verifiedAt === null || c.verifiedPmsVersion === null)) {
      ctx.addIssue({
        code: 'custom',
        path: ['verifiedAt'],
        message: 'verified capabilities record when and on which PMS version',
      });
    }
    if ((c.tier === 'blocked' || c.tier === 'unsupported') && c.reason === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['reason'],
        message: `${c.tier} capabilities need a reason`,
      });
    }
    if ((c.tier === 'blocked' || c.tier === 'unsupported') && c.verified) {
      ctx.addIssue({ code: 'custom', path: ['verified'], message: `${c.tier} cannot be verified` });
    }
  });
export type Capability = z.infer<typeof CapabilitySchema>;

/** Capability manifest snapshot for one office (`pms_link.capability_manifest`). */
export const CapabilityManifestSchema = z
  .object({
    ...LocationOwnedSchema.shape,
    pmsLinkId: IdSchema,
    adapter: PmsAdapterSchema,
    pmsSystem: PmsSystemSchema,
    pmsVersion: z.string().nullable(),
    adapterVersion: z.string().nullable(),
    fetchedAt: InstantSchema,
    /**
     * Writes auto-pause when the PMS version changes until the adapter is
     * re-verified (BUILD-PLAN 2.5.6).
     */
    writesPaused: z.boolean(),
    writesPausedReason: z.string().max(300).nullable(),
    sorStages: z.record(DataClassSchema, SorStageSchema),
    capabilities: z.array(CapabilitySchema),
  })
  .superRefine((m, ctx) => {
    const seen = new Set<string>();
    m.capabilities.forEach((c, i) => {
      if (seen.has(c.commandType)) {
        ctx.addIssue({
          code: 'custom',
          path: ['capabilities', i, 'commandType'],
          message: `duplicate capability "${c.commandType}"`,
        });
      }
      seen.add(c.commandType);
    });
    if (m.writesPaused && m.writesPausedReason === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['writesPausedReason'],
        message: 'paused writes need a reason',
      });
    }
  });
export type CapabilityManifest = z.infer<typeof CapabilityManifestSchema>;

export type CapabilityDecision =
  | { allowed: true; capability: Capability }
  | {
      allowed: false;
      /** Maps to the API error code (422 `capability_unavailable` or `writes_paused`). */
      code: 'capability_unavailable' | 'writes_paused';
      reason: string;
      capability: Capability | null;
    };

/**
 * Fail-closed capability gate (M02 acceptance: a command on a capability not
 * marked stable and verified is rejected with 422 `capability_unavailable`).
 * `changedFields` lets a partial command through when it does not touch an
 * ignored field (OD: move allowed, resize refused).
 */
export function checkCapability(
  manifest: Pick<
    CapabilityManifest,
    'writesPaused' | 'writesPausedReason' | 'capabilities' | 'pmsVersion'
  >,
  commandType: CommandType,
  changedFields?: readonly string[],
): CapabilityDecision {
  const capability = manifest.capabilities.find((c) => c.commandType === commandType) ?? null;
  if (manifest.writesPaused) {
    return {
      allowed: false,
      code: 'writes_paused',
      reason: manifest.writesPausedReason ?? 'Writes to this office are paused',
      capability,
    };
  }
  if (capability === null) {
    return {
      allowed: false,
      code: 'capability_unavailable',
      reason: 'Not yet available for this office’s PMS',
      capability: null,
    };
  }
  if (capability.tier !== 'stable' || !capability.verified) {
    return {
      allowed: false,
      code: 'capability_unavailable',
      reason:
        capability.reason ??
        (capability.tier === 'stable'
          ? 'Not yet verified for this office’s PMS version'
          : 'Not yet available for this office’s PMS'),
      capability,
    };
  }
  // Verification is per PMS version (BUILD-PLAN 2.5.3 "until a write is
  // proven on a sandbox OD of the same version"; 2.5.6 auto-pause on version
  // change). A capability verified on another or unknown version is refused
  // even if the connector has not paused writes yet.
  if (manifest.pmsVersion === null || capability.verifiedPmsVersion !== manifest.pmsVersion) {
    return {
      allowed: false,
      code: 'capability_unavailable',
      reason: 'Not yet verified for this office’s PMS version',
      capability,
    };
  }
  // Fail closed: when the adapter ignores some fields, the caller must say
  // which fields it changes, or a resize could slip through as a "move".
  if (changedFields === undefined) {
    if (capability.ignoredFields.length > 0) {
      return {
        allowed: false,
        code: 'capability_unavailable',
        reason: `This office’s PMS ignores changes to: ${capability.ignoredFields.join(', ')}`,
        capability,
      };
    }
    return { allowed: true, capability };
  }
  const ignored = changedFields.filter((f) => capability.ignoredFields.includes(f));
  if (ignored.length > 0) {
    return {
      allowed: false,
      code: 'capability_unavailable',
      reason: `This office’s PMS ignores changes to: ${ignored.join(', ')}`,
      capability,
    };
  }
  return { allowed: true, capability };
}

/* ------------------------------------------------------------------------ */
/* Conflicts (BUILD-PLAN 2.5.4 step 5, 2.5.5)                                */
/* ------------------------------------------------------------------------ */

export const ConflictStatusSchema = z.enum(['open', 'resolved', 'dismissed']);
export type ConflictStatus = z.infer<typeof ConflictStatusSchema>;

export const ConflictResolutionSchema = z.enum(['ours', 'theirs', 'merged']);
export type ConflictResolution = z.infer<typeof ConflictResolutionSchema>;

/** Field values keyed by field name; null when that side has no row (delete). */
const SideSchema = z.record(z.string(), z.unknown()).nullable();

/**
 * A same-field clash between Velora (`ours`) and the PMS (`theirs`) relative
 * to the last common read (`base`). Disjoint field edits auto-merge and never
 * produce a conflict (BUILD-PLAN 2.5.5 Demographics).
 */
export const ConflictSchema = z
  .object({
    id: IdSchema,
    ...LocationOwnedSchema.shape,
    entity: EntityNameSchema,
    rowId: IdSchema.nullable(),
    /** The command that was rejected, when the conflict came from write-back. */
    commandId: IdSchema.nullable(),
    fields: z.array(z.string().min(1)),
    base: SideSchema,
    ours: SideSchema,
    theirs: SideSchema,
    status: ConflictStatusSchema,
    resolution: ConflictResolutionSchema.nullable(),
    resolverUserId: IdSchema.nullable(),
    resolvedAt: InstantSchema.nullable(),
    createdAt: InstantSchema,
  })
  .superRefine((c, ctx) => {
    if (c.status === 'open') {
      if (c.resolution !== null || c.resolverUserId !== null || c.resolvedAt !== null) {
        ctx.addIssue({
          code: 'custom',
          path: ['status'],
          message: 'open conflicts have no resolution',
        });
      }
    } else if (c.resolverUserId === null || c.resolvedAt === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['resolvedAt'],
        message: 'closed conflicts record who resolved them and when',
      });
    }
    if (c.status === 'resolved' && c.resolution === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['resolution'],
        message: 'resolved conflicts need a resolution',
      });
    }
  });
export type Conflict = z.infer<typeof ConflictSchema>;

/** Body of `POST /v1/sync/conflicts/:id/resolve`. */
export const ResolveConflictRequestSchema = z
  .object({
    resolution: ConflictResolutionSchema,
    /** Required for `merged`: the final value of every conflicting field. */
    mergedValues: z.record(z.string(), z.unknown()).optional(),
    expectedVersion: RowVersionSchema,
    reason: z.string().max(500).optional(),
  })
  .strict()
  .superRefine((r, ctx) => {
    if (r.resolution === 'merged' && r.mergedValues === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['mergedValues'],
        message: 'merged resolution needs values',
      });
    }
    if (r.resolution !== 'merged' && r.mergedValues !== undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['mergedValues'],
        message: 'mergedValues is only allowed with a merged resolution',
      });
    }
  });
export type ResolveConflictRequest = z.infer<typeof ResolveConflictRequestSchema>;

/* ------------------------------------------------------------------------ */
/* Paging                                                                    */
/* ------------------------------------------------------------------------ */

/** Opaque keyset cursor. Never an OFFSET (PulseSync OFFSET clamp history, 2.5.2). */
export const CursorSchema = z.string().min(1).max(512);

/** `{ items, nextCursor }` page wrapper used by list endpoints. */
export function pageOf<T extends z.ZodType>(item: T) {
  return z.object({ items: z.array(item), nextCursor: CursorSchema.nullable() });
}

/* ------------------------------------------------------------------------ */
/* Field-level three-way merge (BUILD-PLAN 2.5.5 Demographics)               */
/* ------------------------------------------------------------------------ */

export interface MergeOutcome {
  /** Fields to write: ours where only we changed, theirs where only they changed. */
  merged: Record<string, unknown>;
  /** Fields both sides changed to different values; these open a `Conflict`. */
  conflicts: string[];
}

/** JSON with object keys sorted, so `{a,b}` and `{b,a}` compare equal. */
function canonicalJson(v: unknown): string | undefined {
  if (Array.isArray(v)) return `[${v.map((x) => canonicalJson(x) ?? 'null').join(',')}]`;
  if (v !== null && typeof v === 'object' && !(v instanceof Date)) {
    const o = v as Record<string, unknown>;
    const parts = Object.keys(o)
      .sort()
      .flatMap((k) => {
        const c = canonicalJson(o[k]);
        return c === undefined ? [] : [`${JSON.stringify(k)}:${c}`];
      });
    return `{${parts.join(',')}}`;
  }
  return JSON.stringify(v);
}

function sameValue(a: unknown, b: unknown): boolean {
  return canonicalJson(a) === canonicalJson(b);
}

const hasOwn = (o: object, k: string): boolean => Object.prototype.hasOwnProperty.call(o, k);

function own(o: Readonly<Record<string, unknown>>, k: string): unknown {
  return hasOwn(o, k) ? o[k] : undefined;
}

/**
 * Merge Velora's edit (`ours`) with a concurrent PMS edit (`theirs`) relative
 * to the last common read (`base`), field by field. Disjoint edits merge; the
 * same field changed to different values on both sides is a conflict; the
 * same field changed to the same value is not. Only fields present in `ours`
 * or `theirs` are considered.
 */
export function threeWayMerge(
  base: Readonly<Record<string, unknown>>,
  ours: Readonly<Record<string, unknown>>,
  theirs: Readonly<Record<string, unknown>>,
): MergeOutcome {
  const merged: Record<string, unknown> = {};
  // Field names come from client JSON; define them as own data properties so
  // a "__proto__" key is data, never a prototype assignment.
  const put = (f: string, v: unknown) =>
    Object.defineProperty(merged, f, {
      value: v,
      enumerable: true,
      writable: true,
      configurable: true,
    });
  const conflicts: string[] = [];
  const fields = new Set([...Object.keys(ours), ...Object.keys(theirs)]);
  for (const f of [...fields].sort()) {
    const b = own(base, f);
    const o = own(ours, f);
    const t = own(theirs, f);
    const oursChanged = hasOwn(ours, f) && !sameValue(o, b);
    const theirsChanged = hasOwn(theirs, f) && !sameValue(t, b);
    if (oursChanged && theirsChanged) {
      if (sameValue(o, t)) put(f, o);
      else conflicts.push(f);
    } else if (oursChanged) {
      put(f, o);
    } else if (theirsChanged) {
      put(f, t);
    }
  }
  return { merged, conflicts };
}

/* ------------------------------------------------------------------------ */
/* Sync API bodies (WP1-SYNC, `/v1/sync/*`)                                  */
/* ------------------------------------------------------------------------ */

/**
 * Body of `POST /v1/sync/commands`: a module (or the UI) asks for a PMS
 * write. `base` is the field values the user saw (for the three-way merge
 * when the PMS changed in between); `optimistic` lists fields the caller
 * already wrote to the Velora row, so a rejection can revert them.
 */
export const SubmitCommandRequestSchema = z
  .object({
    type: CommandTypeSchema,
    idempotencyKey: IdempotencyKeySchema,
    target: CommandTargetSchema,
    changes: CommandChangesSchema,
    base: z.record(z.string(), z.unknown()).nullable().optional(),
    optimistic: z.record(z.string(), z.unknown()).nullable().optional(),
  })
  .strict();
export type SubmitCommandRequest = z.infer<typeof SubmitCommandRequestSchema>;

/** `GET /v1/sync/commands` query. */
export const CommandListQuerySchema = z.object({
  status: z
    .string()
    .optional()
    .transform((s) => (s ? s.split(',').filter(Boolean) : undefined))
    .pipe(z.array(CommandStatusSchema).optional()),
  entity: EntityNameSchema.optional(),
  rowId: IdSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: CursorSchema.optional(),
});
export type CommandListQuery = z.infer<typeof CommandListQuerySchema>;

/** A command as the API lists it: the outbox row plus its record-level state. */
export const CommandViewSchema = z.object({
  command: CommandSchema,
  writeBack: WriteBackStateSchema,
  /** True while an `unknown` command waits for a human (money, or read-back pending). */
  needsReview: z.boolean(),
});
export type CommandView = z.infer<typeof CommandViewSchema>;

export const CommandPageSchema = pageOf(CommandViewSchema);
export type CommandPage = z.infer<typeof CommandPageSchema>;

/** Body of `POST /v1/sync/run` (sync now). Omitted offices mean the request scope. */
export const SyncRunRequestSchema = z
  .object({
    locationIds: z.array(IdSchema).max(50).optional(),
    entities: z.array(EntityNameSchema).max(20).optional(),
    /** Ignore cursors and pull everything (repairs drift). */
    full: z.boolean().default(false),
  })
  .strict();
export type SyncRunRequest = z.infer<typeof SyncRunRequestSchema>;

export const EntityRunResultSchema = z.object({
  entity: EntityNameSchema,
  status: z.enum(['succeeded', 'failed', 'skipped']),
  rowsUpserted: z.number().int().nonnegative(),
  rowsDeleted: z.number().int().nonnegative(),
  issues: z.number().int().nonnegative(),
  durationMs: z.number().int().nonnegative(),
  error: z.string().max(300).nullable(),
});
export type EntityRunResult = z.infer<typeof EntityRunResultSchema>;

/** Body returned by `POST /v1/sync/run`. */
export const SyncRunResultSchema = z.object({
  offices: z.array(
    z.object({
      locationId: IdSchema,
      entities: z.array(EntityRunResultSchema),
      commandsDispatched: z.number().int().nonnegative(),
      acksApplied: z.number().int().nonnegative(),
    }),
  ),
});
export type SyncRunResult = z.infer<typeof SyncRunResultSchema>;

/** Body of `PUT /v1/sync/connectors/:locationId/writes`. */
export const WritePauseRequestSchema = z
  .object({
    paused: z.boolean(),
    reason: z.string().trim().min(3).max(300).optional(),
  })
  .strict()
  .superRefine((r, ctx) => {
    if (r.paused && r.reason === undefined) {
      ctx.addIssue({ code: 'custom', path: ['reason'], message: 'pausing writes needs a reason' });
    }
  });
export type WritePauseRequest = z.infer<typeof WritePauseRequestSchema>;

/**
 * Body of `POST /v1/sync/connectors/:locationId/verifications`: an admin
 * records that a command was proven on this office's PMS version (sandbox
 * write plus read-back). Only stable commands can be verified.
 */
export const VerifyCapabilityRequestSchema = z
  .object({
    commandType: CommandTypeSchema,
    evidence: z.string().trim().min(3).max(1000),
  })
  .strict();
export type VerifyCapabilityRequest = z.infer<typeof VerifyCapabilityRequestSchema>;

export const CapabilityVerificationSchema = z.object({
  id: IdSchema,
  commandType: CommandTypeSchema,
  pmsVersion: z.string(),
  verifiedAt: InstantSchema,
  verifiedByName: z.string(),
  evidence: z.string(),
});
export type CapabilityVerification = z.infer<typeof CapabilityVerificationSchema>;

/** `GET /v1/sync/conflicts` query. */
export const ConflictListQuerySchema = z.object({
  status: ConflictStatusSchema.default('open'),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type ConflictListQuery = z.infer<typeof ConflictListQuerySchema>;

/** A conflict with its row version (for `ResolveConflictRequest.expectedVersion`). */
export const ConflictViewSchema = z.object({
  conflict: ConflictSchema,
  version: RowVersionSchema,
  commandType: CommandTypeSchema.nullable(),
});
export type ConflictView = z.infer<typeof ConflictViewSchema>;

export const ConflictListSchema = z.object({ items: z.array(ConflictViewSchema) });
export type ConflictList = z.infer<typeof ConflictListSchema>;

/* ------------------------------------------------------------------------ */
/* Data availability (V06, wave 0)                                           */
/* ------------------------------------------------------------------------ */

/**
 * Data feeds a screen may depend on that the PMS connection does not always
 * deliver. A screen that shows one of these asks `dataAvailability` (or
 * `GET /v1/sync/availability`) first and renders an honest "Not synced from
 * <PMS>" state instead of a fake "$0", "No medical alerts" or "Self pay".
 *
 * The table is what PulseSync v1 serves today (mailbox gaps reply 2026-10-09
 * and the 2026-10-14 audit). When a PulseSync route ships (PS-01..PS-07) the
 * feed moves to `synced` here, in one place.
 */
export const DATA_FEEDS = [
  'ledger',
  'insurance',
  'medicalAlerts',
  'clinicalNotes',
  'procedureNotes',
  'perio',
  'households',
  'demographicsExtended',
  'firstVisit',
  'apptProcedureLink',
  'flow',
  'officeHours',
  'providerSchedules',
  'appointmentTypes',
  'toothStates',
  'feeSchedules',
  'images',
  'apptCreatedAt',
  'claimLines',
  'recallTypeNames',
  'providerColors',
  'scheduledProduction',
  'commlog',
  'documents',
] as const;
export const DataFeedSchema = z.enum(DATA_FEEDS);
export type DataFeed = z.infer<typeof DataFeedSchema>;

/**
 * - `synced`: the feed arrives from the PMS.
 * - `partial`: some of it arrives, or Velora derives it (the reason says how).
 * - `not_synced`: nothing arrives; the screen must say so.
 */
export const DATA_FEED_STATES = ['synced', 'partial', 'not_synced'] as const;
export const DataFeedStateSchema = z.enum(DATA_FEED_STATES);
export type DataFeedState = z.infer<typeof DataFeedStateSchema>;

export const FeedAvailabilitySchema = z.object({
  state: DataFeedStateSchema,
  /** One line for staff, no PHI, no PMS name (the UI adds "from Dentrix"). */
  reason: z.string().max(300),
});
export type FeedAvailability = z.infer<typeof FeedAvailabilitySchema>;

export type DataAvailability = Record<DataFeed, FeedAvailability>;

/** The link facts availability depends on. `null`: the office has no PMS link. */
export interface AvailabilityLink {
  adapter: PmsAdapter;
  pmsSystem: PmsSystem;
  /**
   * Feeds a PulseSync v2 reader has actually delivered for this link (PS-01
   * ledger, PS-02 insurance, PS-05 schedules, PS-06 recall types, PS-04
   * feeds), from the link's sync cursors. Overrides the static table, so a
   * feed flips to `synced` only once rows arrived, and back when they stop.
   */
  live?: Readonly<Partial<Record<DataFeed, FeedAvailability>>>;
}

type FeedTable = Readonly<Record<DataFeed, FeedAvailability>>;

const NOT = (reason: string): FeedAvailability => ({ state: 'not_synced', reason });
const PARTIAL = (reason: string): FeedAvailability => ({ state: 'partial', reason });
const SYNCED = (reason: string): FeedAvailability => ({ state: 'synced', reason });

/** PulseSync v1, common to every PMS (overrides per PMS below). */
const V1_FEEDS: FeedTable = {
  ledger: NOT('PulseSync has no ledger feed yet (charges, payments, adjustments, balances).'),
  insurance: NOT('PulseSync has no insurance plan, subscriber or benefits feed yet.'),
  medicalAlerts: NOT('PulseSync has no medical alerts, allergies or premedication feed yet.'),
  clinicalNotes: NOT('PulseSync has no clinical notes feed yet.'),
  procedureNotes: NOT('PulseSync does not send procedure notes yet.'),
  perio: NOT('PulseSync has no perio chart feed yet.'),
  households: NOT('PulseSync does not send the guarantor or family links yet.'),
  demographicsExtended: PARTIAL(
    'Name, birth date, phones, email and address only; gender, preferred name, primary provider and chart number are not sent yet.',
  ),
  firstVisit: NOT('PulseSync does not send the first visit date yet.'),
  apptProcedureLink: SYNCED('Procedures carry their appointment.'),
  flow: NOT('PulseSync does not send arrival, seated or dismissed times yet.'),
  officeHours: NOT('PulseSync does not send office hours yet.'),
  providerSchedules: NOT('PulseSync does not send provider schedules or blockouts yet.'),
  appointmentTypes: PARTIAL(
    'Built from appointment reasons; the appointment type list is not sent yet.',
  ),
  toothStates: NOT('PulseSync does not send tooth conditions (missing, primary) yet.'),
  feeSchedules: NOT('PulseSync does not send fee schedules yet; fees are per procedure only.'),
  images: PARTIAL(
    'PulseSync sends no images; images come from the imaging connection when one is set up.',
  ),
  apptCreatedAt: NOT('PulseSync does not send when an appointment was booked yet.'),
  claimLines: NOT('PulseSync sends claim totals only, not the procedures on a claim, yet.'),
  recallTypeNames: PARTIAL('Recall type names arrive only when the PMS sends them.'),
  providerColors: PARTIAL(
    'Provider colours are assigned by Velora; the PMS colours are not sent yet.',
  ),
  scheduledProduction: NOT('PulseSync does not send the scheduled amount of an appointment yet.'),
  commlog: NOT('The PMS communication log is not read yet.'),
  documents: NOT('The PMS document list is not read yet.'),
};

const V1_BY_PMS: Readonly<Record<PmsSystem, Partial<FeedTable>>> = {
  dentrix: {
    apptProcedureLink: NOT('Dentrix procedures arrive without their appointment.'),
    recallTypeNames: NOT('Dentrix recall types arrive as numbers only, without names.'),
  },
  opendental: {},
  eaglesoft: {
    apptProcedureLink: NOT('Eaglesoft procedures arrive without their appointment.'),
  },
};

const MOCK_FEEDS = Object.fromEntries(
  DATA_FEEDS.map((f) => [f, SYNCED('Development simulator: every feed is simulated.')]),
) as FeedTable;

const NO_LINK = Object.fromEntries(
  DATA_FEEDS.map((f) => [f, NOT('This office is not connected to a PMS.')]),
) as FeedTable;

/**
 * What the office's PMS connection delivers, per feed. Pure: the answer
 * depends only on the adapter and the PMS. PulseSync v2 is not available
 * yet, so it answers like v1 until its feed list is confirmed.
 */
export function dataAvailability(link: AvailabilityLink | null): DataAvailability {
  if (link === null) return { ...NO_LINK };
  if (link.adapter === 'mock') return { ...MOCK_FEEDS };
  return { ...V1_FEEDS, ...V1_BY_PMS[link.pmsSystem], ...(link.live ?? {}) };
}

/** One office in `GET /v1/sync/availability`. */
export const OfficeAvailabilitySchema = z.object({
  locationId: IdSchema,
  /** Null when the office has no PMS link. */
  adapter: PmsAdapterSchema.nullable(),
  pmsSystem: PmsSystemSchema.nullable(),
  feeds: z.record(DataFeedSchema, FeedAvailabilitySchema),
});
export type OfficeAvailability = z.infer<typeof OfficeAvailabilitySchema>;

/** Body of `GET /v1/sync/availability` (offices in the request scope). */
export const DataAvailabilityResponseSchema = z.object({
  offices: z.array(OfficeAvailabilitySchema),
});
export type DataAvailabilityResponse = z.infer<typeof DataAvailabilityResponseSchema>;
